import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import ArrowLineRight from '@icons/arrow/ArrowLineRight';
import { ReactNode } from 'react';

import ArrowIcon from './ArrowIcon';
import useSlideHandle, { HANDLE_WIDTH, SlideBarStatus } from './hooks/useSlideHandle';
import useWidth from './hooks/useWidth';

interface Props {
    children?: ReactNode;
    onConfirm?: () =>void;
    width?: number | string;
}

const SIZE = 56;

function SlideBar({ children, onConfirm, width = '100%' }:Props) {
    const { width: containerWidth, ref } = useWidth();
    const {
        percent, handleTouchEnd, handleTouchStart, handleTouchMove,
        status, translateX,
    } = useSlideHandle({
        containerWidth, onConfirm,
    });
    return (
        <Container ref={ref}
            style={{ width }} >
            <Text color={colors.textThird}
                size='headline'
                fontWeight='bold'>
                {children}
            </Text>
            <ProgressWrapper status={status}
                style={{ width: `calc(${percent * 100}%  + ${HANDLE_WIDTH / 2}px)` }} >
                <Progress style={{ width: containerWidth }} >
                    <Text color={colors.textReverse}
                        size='headline'
                        fontWeight='bold'>
                        {children}
                    </Text>
                </Progress>
            </ProgressWrapper>
            <Handle status={status}
                style={{
                    transform: `translateX(${translateX}px)`,
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd} >
                <ArrowLineRight color={colors.textReverse}
                    size={24} />
            </Handle>
        </Container>
    );
}

const Container = styled.div`
    align-items: center;
    background-color: var(--color-button-unselected);
    border-radius: var(--radius-lg);
    display: flex;
    height: ${SIZE}px;
    justify-content: center;
    overflow: hidden;
    position: relative;
`;

const Progress = styled.div`
    align-items: center;
    background-color: var(--color-primary);
    border-radius: var(--radius-lg);
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
`;

const ProgressWrapper = styled.div<{
    status: SlideBarStatus;
}>`
    height: 100%;
    left: 0;
    overflow: hidden;
    position: absolute;
    ${({ status }) => status !== 'dragging' && 'transition: 150ms linear;'}
`;

const Handle = styled.div<{
    status: SlideBarStatus;
}>`
    align-items: center;
    background-color: var(--color-primary);
    border-radius: var(--radius-lg);
    box-shadow: -1px 0 1px 0 rgba(0, 0, 0, 0.25);
    display: flex;
    height: ${SIZE}px;
    justify-content: center;
    left: 0;
    position: absolute;
    width: ${SIZE}px;
    ${({ status }) => status === 'idle' && 'transition: 150ms linear;'}
`;

export default SlideBar;
